import React, {useEffect, useState} from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    IconButton,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Divider,
    Tooltip
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    StickyNote2 as NoteIcon,
    Event as EventIcon
} from "@mui/icons-material";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import LoadingButton from "@mui/lab/LoadingButton";
import {ApiDetails} from "../../../../Constants";
import {showSnackbar} from "../../../../components/SnackBar/SnackBar";
import { handle401Unauthorized, handle403Forbidden } from '../../../../utils/authErrorHandler';


const TenantNotes = ({tenantID, landlordID, token, isVacated}) => {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [addNoteModal, setAddNoteModal] = useState(false);
    const [editNoteModal, setEditNoteModal] = useState(false);
    const [deleteNoteModal, setDeleteNoteModal] = useState(false);
    const [selectedNote, setSelectedNote] = useState(null);
    const [noteData, setNoteData] = useState({
        title: "",
        content: "",
        reminder_date: null
    });
    const [savingNote, setSavingNote] = useState(false);
    const [deletingNote, setDeletingNote] = useState(false);

    useEffect(() => {
        fetchNotes();
    }, [tenantID]);

    const fetchNotes = () => {
        setLoading(true);
        fetch(ApiDetails + "pegasus/visionary/tenant/notes/fetch", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": token
            },
            body: JSON.stringify({
                tenant_id: tenantID
            }),
        })
            .then(async (response) => {
                // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
                if (response.status === 200) {
                    const data = await response.json();
                    setNotes(data.notes || []);
                }
                setLoading(false);
            })
            .catch((err) => {
                console.log(err);
                setLoading(false);
            });
    };

    const handleAddNote = () => {
        if (!noteData.title.trim() || !noteData.content.trim()) {
            showSnackbar({
                title: "Error!",
                message: "Please fill in both title and content.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        setSavingNote(true);

        // Format reminder_date if present
        const formattedReminderDate = noteData.reminder_date
            ? dayjs(noteData.reminder_date).format('YYYY-MM-DD')
            : null;

        fetch(ApiDetails + "pegasus/visionary/tenant/notes/add", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": token
            },
            body: JSON.stringify({
                tenant_id: tenantID,
                landlord_id: landlordID,
                title: noteData.title,
                content: noteData.content,
                reminder_date: formattedReminderDate
            }),
        })
            .then(async (response) => {
                // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
                if (response.status === 200) {
                    showSnackbar({
                        title: "Success!",
                        message: "Note added successfully.",
                        type: "success",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                    setAddNoteModal(false);
                    setNoteData({title: "", content: "", reminder_date: null});
                    fetchNotes();
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: "Failed to add note.",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
                setSavingNote(false);
            })
            .catch((err) => {
                console.log(err);
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                setSavingNote(false);
            });
    };

    const handleEditNote = () => {
        if (!noteData.title.trim() || !noteData.content.trim()) {
            showSnackbar({
                title: "Error!",
                message: "Please fill in both title and content.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        setSavingNote(true);

        // Format reminder_date if present
        const formattedReminderDate = noteData.reminder_date
            ? dayjs(noteData.reminder_date).format('YYYY-MM-DD')
            : null;

        fetch(ApiDetails + "pegasus/visionary/tenant/notes/update", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": token
            },
            body: JSON.stringify({
                note_id: selectedNote.id,
                title: noteData.title,
                content: noteData.content,
                reminder_date: formattedReminderDate
            }),
        })
            .then(async (response) => {
                // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
                if (response.status === 200) {
                    showSnackbar({
                        title: "Success!",
                        message: "Note updated successfully.",
                        type: "success",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                    setEditNoteModal(false);
                    setNoteData({title: "", content: "", reminder_date: null});
                    setSelectedNote(null);
                    fetchNotes();
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: "Failed to update note.",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
                setSavingNote(false);
            })
            .catch((err) => {
                console.log(err);
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                setSavingNote(false);
            });
    };

    const handleDeleteNote = () => {
        setDeletingNote(true);

        fetch(ApiDetails + "pegasus/visionary/tenant/notes/delete", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": token
            },
            body: JSON.stringify({
                note_id: selectedNote.id
            }),
        })
            .then(async (response) => {
                // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
                if (response.status === 200) {
                    showSnackbar({
                        title: "Success!",
                        message: "Note deleted successfully.",
                        type: "success",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                    setDeleteNoteModal(false);
                    setSelectedNote(null);
                    fetchNotes();
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: "Failed to delete note.",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
                setDeletingNote(false);
            })
            .catch((err) => {
                console.log(err);
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                setDeletingNote(false);
            });
    };

    const openEditModal = (note) => {
        setSelectedNote(note);
        setNoteData({
            title: note.title,
            content: note.content,
            reminder_date: note.reminder_date ? dayjs(note.reminder_date) : null
        });
        setEditNoteModal(true);
    };

    const openDeleteModal = (note) => {
        setSelectedNote(note);
        setDeleteNoteModal(true);
    };

    const handleClose = () => {
        setAddNoteModal(false);
        setEditNoteModal(false);
        setDeleteNoteModal(false);
        setNoteData({title: "", content: "", reminder_date: null});
        setSelectedNote(null);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Box>
            {/* Add Note Button */}
            <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 3}}>
                <Tooltip title={isVacated ? "Cannot add notes for a vacated tenant" : ""} arrow>
                    <span>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon/>}
                            onClick={() => setAddNoteModal(true)}
                            disabled={isVacated}
                            sx={{
                                bgcolor: '#398DD2',
                                textTransform: 'none',
                                fontSize: '14px',
                                fontWeight: 500,
                                borderRadius: 2,
                                px: 3,
                                '&:hover': {
                                    bgcolor: '#2c6ba0'
                                }
                            }}
                        >
                            Add Note
                        </Button>
                    </span>
                </Tooltip>
            </Box>

            {/* Notes Grid */}
            {loading ? (
                <Box sx={{textAlign: 'center', py: 4}}>
                    <Typography>Loading notes...</Typography>
                </Box>
            ) : notes.length === 0 ? (
                <Box sx={{
                    textAlign: 'center',
                    py: 6,
                    bgcolor: '#f5f5f5',
                    borderRadius: 2,
                    border: '1px dashed #ccc'
                }}>
                    <NoteIcon sx={{fontSize: 48, color: '#ccc', mb: 2}}/>
                    <Typography variant="body1" color="textSecondary">
                        No notes yet. Add your first note to keep track of important information.
                    </Typography>
                </Box>
            ) : (
                <Grid container spacing={2}>
                    {notes.map((note) => (
                        <Grid item xs={12} md={6} key={note.id}>
                            <Card sx={{
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                borderRadius: 2,
                                transition: 'box-shadow 0.3s ease',
                                '&:hover': {
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
                                }
                            }}>
                                <CardContent>
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        mb: 1
                                    }}>
                                        <Typography variant="h6" sx={{fontWeight: 600, color: '#2c3e50', mb: 1}}>
                                            {note.title}
                                        </Typography>
                                        <Box>
                                            <Tooltip title="Edit note">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => openEditModal(note)}
                                                    disabled={isVacated}
                                                    sx={{color: '#398DD2'}}
                                                >
                                                    <EditIcon fontSize="small"/>
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete note">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => openDeleteModal(note)}
                                                    disabled={isVacated}
                                                    sx={{color: '#d32f2f'}}
                                                >
                                                    <DeleteIcon fontSize="small"/>
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                    <Divider sx={{mb: 2}}/>
                                    <Typography variant="body2" sx={{color: '#6c757d', mb: 2, whiteSpace: 'pre-wrap'}}>
                                        {note.content}
                                    </Typography>
                                    <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                                        {note.reminder_date && (
                                            <Chip
                                                icon={<EventIcon/>}
                                                label={`Reminder: ${dayjs(note.reminder_date).format('MMM DD, YYYY')}`}
                                                size="small"
                                                sx={{
                                                    fontSize: '11px',
                                                    bgcolor: '#fff3e0',
                                                    color: '#e65100',
                                                    fontWeight: 500
                                                }}
                                            />
                                        )}
                                        <Chip
                                            label={formatDate(note.created_at)}
                                            size="small"
                                            sx={{
                                                fontSize: '11px',
                                                bgcolor: '#e3f2fd',
                                                color: '#1976d2'
                                            }}
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Add Note Modal */}
            <Dialog
                open={addNoteModal}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{color: '#398DD2', fontWeight: 600}}>
                    Add New Note
                </DialogTitle>
                <DialogContent>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, mt: 2}}>
                        <TextField
                            label="Title"
                            fullWidth
                            value={noteData.title}
                            onChange={(e) => setNoteData({...noteData, title: e.target.value})}
                            placeholder="Enter note title..."
                        />
                        <TextField
                            label="Content"
                            fullWidth
                            multiline
                            rows={6}
                            value={noteData.content}
                            onChange={(e) => setNoteData({...noteData, content: e.target.value})}
                            placeholder="Enter note content..."
                        />
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Reminder Date (Optional)"
                                value={noteData.reminder_date}
                                onChange={(newValue) => setNoteData({...noteData, reminder_date: newValue})}
                                minDate={dayjs()}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        variant="outlined"
                                        helperText="Set a reminder to receive an SMS notification"
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "8px",
                                            },
                                        }}
                                    />
                                )}
                            />
                        </LocalizationProvider>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} startIcon={<CancelIcon/>}>
                        Cancel
                    </Button>
                    <LoadingButton
                        onClick={handleAddNote}
                        loading={savingNote}
                        startIcon={<SaveIcon/>}
                        variant="contained"
                        sx={{
                            bgcolor: '#398DD2',
                            '&:hover': {bgcolor: '#2c6ba0'}
                        }}
                    >
                        Save Note
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            {/* Edit Note Modal */}
            <Dialog
                open={editNoteModal}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{color: '#398DD2', fontWeight: 600}}>
                    Edit Note
                </DialogTitle>
                <DialogContent>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, mt: 2}}>
                        <TextField
                            label="Title"
                            fullWidth
                            value={noteData.title}
                            onChange={(e) => setNoteData({...noteData, title: e.target.value})}
                            placeholder="Enter note title..."
                        />
                        <TextField
                            label="Content"
                            fullWidth
                            multiline
                            rows={6}
                            value={noteData.content}
                            onChange={(e) => setNoteData({...noteData, content: e.target.value})}
                            placeholder="Enter note content..."
                        />
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Reminder Date (Optional)"
                                value={noteData.reminder_date}
                                onChange={(newValue) => setNoteData({...noteData, reminder_date: newValue})}
                                minDate={dayjs()}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        variant="outlined"
                                        helperText="Set a reminder to receive an SMS notification"
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "8px",
                                            },
                                        }}
                                    />
                                )}
                            />
                        </LocalizationProvider>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} startIcon={<CancelIcon/>}>
                        Cancel
                    </Button>
                    <LoadingButton
                        onClick={handleEditNote}
                        loading={savingNote}
                        startIcon={<SaveIcon/>}
                        variant="contained"
                        sx={{
                            bgcolor: '#398DD2',
                            '&:hover': {bgcolor: '#2c6ba0'}
                        }}
                    >
                        Update Note
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            {/* Delete Note Modal */}
            <Dialog
                open={deleteNoteModal}
                onClose={handleClose}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{color: '#d32f2f', fontWeight: 600}}>
                    Delete Note
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this note? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>
                        Cancel
                    </Button>
                    <LoadingButton
                        onClick={handleDeleteNote}
                        loading={deletingNote}
                        startIcon={<DeleteIcon/>}
                        variant="contained"
                        color="error"
                    >
                        Delete
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TenantNotes;
